import { useParams } from 'react-router-dom';
import { Card, Spinner, Button } from '@/components/ui';
import { PageHeader } from '@/components/layout';
import { useWeeklyReportDetail } from '@/api/useWeeklyReports';
import { ROUTES } from '@/constants/routes';
import styles from './WeeklyReport.module.css';

export function WeeklyReportView() {
    const { id } = useParams();
    const { data: report, isLoading } = useWeeklyReportDetail(id || '');

    if (isLoading) {
        return <div className="flex justify-center p-8"><Spinner size="lg" /></div>;
    }

    if (!report) {
        return <div className={styles.container}>Reporte no encontrado</div>;
    }

    // Calcs
    const totalEquipment = report.equipment_details?.length || 0;
    const inoperativeCount = report.equipment_details?.filter((d: any) => d.condition_end === 'inoperative').length || 0;
    const operativeCount = totalEquipment - inoperativeCount;

    // Avg Availability (using 168h base)
    const totalAvailability = report.equipment_details?.reduce((acc: number, d: any) => {
        const hfm = d.hfm_hours || 0;
        const avail = ((168 - hfm) / 168) * 100;
        return acc + avail;
    }, 0) || 0;
    const avgAvailability = totalEquipment > 0 ? (totalAvailability / totalEquipment).toFixed(1) : '100.0';

    return (
        <div className={styles.container}>
            <PageHeader
                title={`Reporte Semanal #${report.id} `}
                subtitle="Vista Detallada"
                backPath={ROUTES.reportsLibrary}
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="primary"
                            onClick={() => window.print()}
                        >
                            <i className="fa-solid fa-file-pdf mr-2"></i>
                            Descargar PDF
                        </Button>
                    </div>
                }
            />

            <Card className={styles.stepCard} variant="full">
                {/* Executive Metrics Grid - Top Level Stats */}
                <div className={styles.summaryGrid}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Total Equipos</span>
                        <span className={styles.summaryValue}>{totalEquipment}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Disponibilidad Prom.</span>
                        <span className={styles.summaryValue} style={{ color: 'var(--color-primary)' }}>
                            {avgAvailability}%
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Operativos</span>
                        <span className={styles.summaryValue} style={{ color: 'var(--color-success)' }}>
                            {operativeCount}
                        </span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Inoperativos</span>
                        <span className={styles.summaryValue} style={{ color: inoperativeCount > 0 ? 'var(--color-error)' : 'var(--text-muted)' }}>
                            {inoperativeCount}
                        </span>
                    </div>
                </div>

                <div className={styles.summaryInfo}>
                    {/* Meta Data Row */}
                    <div className={styles.metaList}>
                        <div className={styles.dataRow}>
                            <span className={styles.dataLabel}>Técnico Responsable</span>
                            <span className={styles.dataValue}>{report.technician_name}</span>
                        </div>
                        <div className={styles.dataRow}>
                            <span className={styles.dataLabel}>Semana</span>
                            <span className={styles.dataValue}>{report.week_start} - {report.week_end}</span>
                        </div>
                        <div className={styles.dataRow}>
                            <span className={styles.dataLabel}>Estado del Reporte</span>
                            <span className={styles.dataValue} style={{ textTransform: 'capitalize' }}>{report.status}</span>
                        </div>
                    </div>

                    <div className={styles.divider}></div>

                    {/* Detailed Equipment List */}
                    <h4 className={styles.categoryTitle}>Detalle por Equipo</h4>

                    {!report.equipment_details || report.equipment_details.length === 0 ? (
                        <div className="p-8 text-center text-gray-400 italic bg-gray-50 rounded border border-dashed border-gray-200">
                            No hay equipos reportados en esta semana.
                        </div>
                    ) : (
                        <div className={styles.detailsList}>
                            {report.equipment_details.map((detail: any, index: number) => {
                                const hfm = detail.hfm_hours || 0;
                                const avail = ((168 - hfm) / 168 * 100).toFixed(1);
                                const isOp = detail.condition_end === 'operative';

                                return (
                                    <div key={index} className={styles.readOnlyItem}>
                                        <div className={styles.readOnlyHeader}>
                                            <span className={styles.detailCode}>{detail.equipment_code}</span>
                                            <span className={isOp ? styles.statusOperative : styles.statusInoperative}>
                                                {isOp ? 'OPERATIVO' : 'INOPERATIVO'}
                                            </span>
                                        </div>
                                        <div className={styles.readOnlyContent}>
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Condición Inicial/Final</span>
                                                <span className={styles.dataValue}>
                                                    {detail.condition_start === 'operative' ? 'Operativo' : 'Inop.'} ➜ {detail.condition_end === 'operative' ? 'Operativo' : 'Inop.'}
                                                </span>
                                            </div>
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Disponibilidad Calc.</span>
                                                <span className={styles.dataValue}>{avail}% ({hfm}h HFM)</span>
                                            </div>

                                            {/* Conditional Failure Block */}
                                            {!isOp && (
                                                <div className={styles.failureBox}>
                                                    <div className={styles.dataRow}>
                                                        <span className={styles.dataLabel}>Falla Reportada</span>
                                                        <span className={styles.dataValue}>
                                                            {detail.system_failed} - {detail.failure_specific}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Observations full width if present */}
                                            {detail.observations && (
                                                <div style={{ gridColumn: '1 / -1' }} className={styles.dataRow}>
                                                    <span className={styles.dataLabel}>Observaciones</span>
                                                    <span className={styles.dataValue}>{detail.observations}</span>
                                                </div>
                                            )}

                                            {detail.evidence_file && (
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <span className={styles.evidenceTag}>
                                                        Evidencia Adjunta
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Additional Works Section */}
                    {(report.electrical_works || report.other_works || report.workshop_notes) && (
                        <>
                            <div className={styles.divider}></div>
                            <h4 className={styles.categoryTitle}>Trabajos Complementarios</h4>

                            {report.electrical_works && (
                                <div className={styles.workBlock}>
                                    <span className={styles.workBlockTitle}>⚡ Trabajos Eléctricos</span>
                                    <p className={styles.workBlockContent}>{report.electrical_works}</p>
                                </div>
                            )}

                            {report.other_works && (
                                <div className={styles.workBlock}>
                                    <span className={styles.workBlockTitle}>🛠️ Otros Trabajos</span>
                                    <p className={styles.workBlockContent}>{report.other_works}</p>
                                </div>
                            )}

                            {report.workshop_notes && (
                                <div className={styles.workBlock}>
                                    <span className={styles.workBlockTitle}>📝 Notas de Taller</span>
                                    <p className={styles.workBlockContent}>{report.workshop_notes}</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
}
